import { Stack, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC, useMemo } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import MUIAlert from 'src/components/ui-component/mui-alert/MUIAlert';
import { useApplication } from 'src/context/ApplicationContext';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, ANTRAGS_FORMULAREN, ANTRAG_TYP, FORM_FIELDS_TYPES, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue, GetCompareMinMaxEqualDate, NonRequired } from 'src/lib/validation/ValidationTypes';
import { checkIfPruefAssitenzNeedsToValidate } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { DisplayDate } from 'src/utils/formats';
import { getLengthOfNumberBeforeAttributes, mapindexbyPath } from 'src/utils/formStruc';
import { checkReadOnlyInUeberarbeitung, getFieldNamefromDataPath } from 'src/utils/helper';
import PruefAssistenz from '../../../../antrag-components/PruefAssistenz';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';

// import schemas
import { getIsAntragsberechtigAlsHochschule } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import AntragSchema from '../../../../../../../lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

const ZeitplanungGlobal: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const partnerNames = getBeteiligte(application);
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path);
	const isSkizze = get(application, P.Allgemein_Typ.path) === ANTRAG_TYP.Skizze;
	const geplanteLauzeit = useMemo(() => {
		const vonDatum = get(application, P.Allgemein_Geplante_Projektlaufzeit_von.path);
		const bisDatum = get(application, P.Allgemein_Geplante_Projektlaufzeit_bis.path);

		if (!vonDatum || !bisDatum) {
			return 'Sie haben die Projektlaufzeit noch nicht eingetragen.';
		}
		if (vonDatum && bisDatum) {
			return (
				<>
					{antragsFormular === ANTRAGS_FORMULAREN.Loewe ?
						<Stack spacing={1}>
							<Typography variant={'body2'}>
								{DisplayDate(get(application, P.Allgemein_Geplante_Projektlaufzeit_von.path))} bis{' '}
								{DisplayDate(get(application, P.Allgemein_Geplante_Projektlaufzeit_bis.path))}
							</Typography>
							{!isSkizze && (
								<Typography variant={'body2'} sx={{ fontWeight: 500 }}>
									Die Arbeitspakete und Meilensteine aus der Zeitplanung ihrer Skizze wurden hier übernommen. Bitte prüfen Sie diese!
								</Typography>
							)}
							<Typography variant={'body2'}>
								Über die Buttons unter der Tabelle können Sie einen Meilenstein oder ein Arbeitspaket hinzufügen. Arbeitspakete stellen
								einzelne Aufgaben dar, Meilensteine markieren zu erreichende Ziele.
							</Typography>
							<Typography variant={'body2'}>
								Bitte stellen Sie einen knappen Zeitplan für geplante Maßnahmen und Aktivitäten auf und definieren Sie die Meilensteine
								während des Projektverlaufs.
							</Typography>
						</Stack>
					:	<>
							<Typography variant={'body2'}>
								{DisplayDate(get(application, P.Allgemein_Geplante_Projektlaufzeit_von.path))} bis{' '}
								{DisplayDate(get(application, P.Allgemein_Geplante_Projektlaufzeit_bis.path))}
							</Typography>
							<br />
							<Typography variant={'body2'}>
								Bitte stellen Sie einen knappen Zeitplan für geplante Maßnahmen und Aktivitäten auf und definieren Sie die Meilensteine
								während des Projektverlaufs.
							</Typography>
							<br />
							<Typography variant={'body2'}>
								Über die Symbole ganz rechts in der Tabelle können Sie Zeilen hinzufügen oder vorhandene Zeilen löschen. Die Bezeichnung der
								Meilensteine ist mit einem Klick in das Feld anpassbar.
							</Typography>
						</>
					}
				</>
			);
		}
		return '';
	}, [application, antragsFormular]);

	return (
		<>
			<ApplicationHeading title='Zeitplanung' description={PLATZHALTER_TEXT} />

			<PruefAssistenz
				statusPath={P.Zeitplanung_Pruefung_Pruefstatus.path}
				hinweisPath={P.Zeitplanung_Pruefung_Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Zeitplanung_Pruefung_Ueberarbeitet_Am.path}
				erledigtPath={P.Zeitplanung_Pruefung_Erledigt.path}
				formStruc={formStrucPruefAssistenz()}
			/>

			<MUIAlert title='Ihre geplante Projektlaufzeit:' message={geplanteLauzeit} />

			<CreateForm
				formStruc={formStruc(application, partnerNames)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
		</>
	);
};

export default ZeitplanungGlobal;

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Zeitplanung_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};

const formStruc = (application: any, partnerNames?: string[]) => {
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path);
	let struc: FormStrucItem[] = [];

	if (antragsFormular === ANTRAGS_FORMULAREN.Loewe) {
		struc = [
			{
				fields: [
					{
						...P.Zeitplanung_Zeitplanung_Block_1,
						type: FORM_FIELDS_TYPES.simpleZeitplanungTable,
						hideColumns: columnsToHide(application),
						fieldProps: customFieldProps(application, partnerNames),
						disabledColumns: ['Zelle_6'], // Typ-Spalte readonly
					},
				],
				userProps: { columnToDivide: 1 },
			},
		];
	} else {
		struc = [
			{
				fields: [
					{
						...P.Zeitplanung_Zeitplanung_Block_1,
						type: FORM_FIELDS_TYPES.editableTable,
						hideColumns: columnsToHide(application),
						fieldProps: customFieldProps(application, partnerNames),
					},
				],
				userProps: { columnToDivide: 1 },
			},
		];
	}

	return struc;
};

const customFormStrucZeitplanung = (application: any, startDatePath: string, typ: string): FormStrucItem[] => {
	const hiddenColumns = columnsToHide(application);
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path);
	// const antragStartDatum = get(application, P.Allgemein_Geplante_Projektlaufzeit_von.path);

	let fields: any[] = [];
	if (antragsFormular === ANTRAGS_FORMULAREN.Loewe) {
		fields = [
			{
				key: 'Zelle_1',
				...P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_1,
			},
			{
				key: 'Zelle_2',
				...P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_2,
			},
			...(typ === 'Meilenstein' ?
				[
					{
						key: 'Zelle_5',
						...P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_5,
					},
				]
				: [
					{
						key: 'Zelle_4',
						...P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_4,
						validation: [
							// { validateFunction: NonRequired },
							{
								validateFunction: GetCompareMinMaxEqualDate,
								onlyValidateOnNonEmptyValue: true,
								params: [P.Allgemein_Geplante_Projektlaufzeit_von.path, P.Allgemein_Geplante_Projektlaufzeit_bis.path],
							},
						],
					},
					{
						key: 'Zelle_5',
						...P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_5,
						validation: [
							// { validateFunction: NonRequired },
							{
								validateFunction: GetCompareMinMaxEqualDate,
								onlyValidateOnNonEmptyValue: true,
								params: [startDatePath, P.Allgemein_Geplante_Projektlaufzeit_bis.path],
							},
						],
					},
				]),
		];
	} else {
		fields = [
			{
				key: 'Zelle_1',
				...P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_1,
			},
			{
				key: 'Zelle_2',
				...P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_2,
			},
			{
				key: 'Zelle_4',
				...P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_4,
				validation: [
					{ validateFunction: NonRequired },
					{
						validateFunction: GetCompareMinMaxEqualDate,
						onlyValidateOnNonEmptyValue: true,
						params: [P.Allgemein_Geplante_Projektlaufzeit_von.path, P.Allgemein_Geplante_Projektlaufzeit_bis.path],
					},
				],
			},
			{
				key: 'Zelle_5',
				...P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_5,
				validation: [
					{ validateFunction: NonRequired },
					{
						validateFunction: GetCompareMinMaxEqualDate,
						onlyValidateOnNonEmptyValue: true,
						params: [startDatePath, P.Allgemein_Geplante_Projektlaufzeit_bis.path],
					},
				],
			},
		];
	}

	// Entferne die Felder, die in hiddenColumns enthalten sind
	const visibleFields = fields.filter((field) => !hiddenColumns.includes(field.key));

	return [
		{
			fields: visibleFields,
		},
	];
};

const getBeteiligte = (application: any): string[] => {
	const antragstellerName = get(application, P.Antragstellende_Organisa_0074063902_licher_Name_Der_Organisation.path);
	const vPName = getFieldNamefromDataPath(P.Verbundpartner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation.path);
	const vPKuerzel = getFieldNamefromDataPath(P.Verbundpartner.schema.attributes.Kuerzel.path);
	const aPName = getFieldNamefromDataPath(P.Assoziierte_Partner.schema.attributes.Rechtsverbindlicher_Name_Der_Organisation.path);
	const antragberechtigtOptionenAusProgramm = get(application, P.Foerderprogramm.schema.attributes.Foerderquote_Antragsberechtigte.path);
	const vPName_arr: string[] = get(application, P.Verbundpartner.path, [])
		.map((vP: any, index: number) => {
			const isPartnerHochschule = getIsAntragsberechtigAlsHochschule(application, index, antragberechtigtOptionenAusProgramm);
			return isPartnerHochschule ? get(vP, vPKuerzel) : get(vP, vPName);
		})
		.filter((partnerName: string) => partnerName && partnerName.trim() !== '');
	const aPName_arr: string[] = get(application, P.Assoziierte_Partner.path, [])
		.map((aP: any) => get(aP, aPName))
		.filter((partnerName: string) => partnerName && partnerName.trim() !== '');
	// const uniqueNames = Array.from(new Set([antragstellerName, ...vPName_arr, aPName_arr]));

	const uniqueNames = Array.from(new Set([antragstellerName, ...vPName_arr, ...aPName_arr])).filter(
		(name) => typeof name === 'string' && name.trim() !== '',
	);

	return uniqueNames;
};

const columnsToHide = (application: any) => {
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path);

	switch (antragsFormular) {
		case ANTRAGS_FORMULAREN.EmobLis:
			return ['Zelle_2', 'Zelle_5', 'Zelle_6', 'Group_id'];
		case ANTRAGS_FORMULAREN.Entwicklungszusammenarbeit:
			return ['Zelle_2', 'Zelle_6', 'Group_id'];
		case ANTRAGS_FORMULAREN.StartUpProjekte:
			return ['Zelle_2', 'Zelle_6', 'Group_id'];
		case ANTRAGS_FORMULAREN.Loewe:
			return ['Group_id']; // seit HA-2204 wird Group_id nicht mehr genutzt
		default:
			return ['Zelle_6', 'Group_id'];
	}
};
const customFieldProps = (application: any, partnerNames?: string[]) => {
	const antragsFormular = get(application, P.Allgemein_Antragsformular.path);

	switch (antragsFormular) {
		case ANTRAGS_FORMULAREN.EmobLis:
			return {
				Zelle_1: { width: '32rem' },
				Zelle_4: { label: 'Fertiggestellt zum' },
				Zelle_6: { label: 'Typ', tooltip: 'Meilenstein oder Arbeitspaket' },
			};
		case ANTRAGS_FORMULAREN.Loewe:
			return {
				Zelle_6: { label: 'Typ', tooltip: 'Meilenstein oder Arbeitspaket', maxWidth: '3rem' },
				Zelle_1: { width: '28rem' },
				Zelle_2: { type: FORM_FIELDS_TYPES.multiSelect, stringMode: true, options: partnerNames ? partnerNames : [] },
				Zelle_4: { width: '11rem' },
				Zelle_5: { width: '11rem' },
			};
		default:
			return {
				Zelle_1: { width: '32rem' },
				Zelle_2: { type: FORM_FIELDS_TYPES.multiSelect, stringMode: true, options: partnerNames ? partnerNames : [] },
			};
	}
};

export const getFormStrucZeitplanungGlobal = (application: any) => {
	let result: any = [];
	const componentTableLength = get(application, P.Zeitplanung_Zeitplanung_Block_1.path, []).length;
	const startDate = P.Zeitplanung_Zeitplanung_Block_1.schema.attributes.Zelle_4.path;
	const parentObject = P.Zeitplanung_Zeitplanung_Block_1;
	const parentObjectPath = get(parentObject, 'path', '');
	let indexLength = getLengthOfNumberBeforeAttributes(parentObjectPath);
	//map index for table
	for (let i = 0; i < componentTableLength; i++) {
		//create index for dynamic param
		const paramPath = parentObjectPath.slice(0, parentObject.path.length) + `.${i}` + startDate.slice(parentObject.path.length - indexLength);
		const typ = get(application, `${parentObjectPath}.${i}.Zelle_6`);
		mapindexbyPath(customFormStrucZeitplanung(application, paramPath, typ), i, parentObject).forEach((element: any) => {
			result.push(element);
		});
	}
	const isHinweisFieldRequired = checkIfPruefAssitenzNeedsToValidate(
		application,
		P.Zeitplanung_Pruefung_Pruefstatus.path,
		P.Zeitplanung_Pruefung_Erledigt.path,
	);

	if (isHinweisFieldRequired) return concat(result, formStrucPruefAssistenz());

	return concat(result);
};
